import { Link } from "react-router-dom";
import { resolveFontSize } from "@/utils/headingSize";
import { buildTextStyle } from "@/components/ui/TextStyleControls";
import { resolveImageUrl } from "@/utils/imageUrl";
import RichHtml from "@/components/ui/RichHtml";

const CARDS_GRID_SHADOW_PRESETS = {
  none: "",
  soft: "0 2px 8px rgba(0,0,0,0.12)",
  medium: "0 6px 16px rgba(0,0,0,0.18)",
  strong: "0 12px 28px rgba(0,0,0,0.3)",
};

/**
 * Cards Grid — admin-managed list of cards (see CardsGridConfigFields in
 * ModuleConfigFields.jsx), up to 16 total. CSS grid caps at 4 columns, so
 * cards wrap automatically into rows of 4; each row's height follows its
 * tallest card (default CSS grid stretch behaviour — no fixed height set).
 */
export default function CardsGridModule({ config = {} }) {
  const cards = Array.isArray(config.cards) ? config.cards.slice(0, 16) : [];
  if (cards.length === 0) return null;

  const headingStyle = {
    ...buildTextStyle(config, "heading_", "heading_font_size"),
    textAlign: config.heading_text_align || "center",
    fontSize: resolveFontSize(config.heading_font_size, "h2"),
  };
  const subheadingStyle = {
    ...buildTextStyle(config, "subheading_", "subheading_font_size"),
    textAlign: config.subheading_text_align || "center",
    fontSize: resolveFontSize(config.subheading_font_size, "h5"),
  };

  const renderCta = (card, idx) => {
    if (!card.cta_text) return null;
    const link = card.cta_url && card.cta_url !== "#" ? card.cta_url : "";
    if (!link) return null;
    const isInternal = link.startsWith("/");
    const className = "inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-md bg-primary transition-transform hover:-translate-y-0.5";
    const style = { color: "var(--button-text, #ffffff)" };
    return isInternal ? (
      <Link to={link} className={className} style={style} data-testid={`cards-grid-public-cta-${idx}`}>{card.cta_text}</Link>
    ) : (
      <a href={link} target="_blank" rel="noopener noreferrer" className={className} style={style} data-testid={`cards-grid-public-cta-${idx}`}>{card.cta_text}</a>
    );
  };

  return (
    <section className="py-16" data-testid="cards-grid-module">
      <div className="w-full">
        {(config.heading || config.subheading) && (
          <div className="mb-10" style={{ textAlign: headingStyle.textAlign }}>
            {config.heading && (
              <h2 className="font-bold" style={headingStyle} data-testid="cards-grid-public-heading">
                {config.heading}
              </h2>
            )}
            {config.subheading && (
              <p
                className="mt-3 max-w-2xl"
                style={{
                  ...subheadingStyle,
                  marginLeft: subheadingStyle.textAlign !== "left" ? "auto" : undefined,
                  marginRight: subheadingStyle.textAlign !== "right" ? "auto" : undefined,
                }}
              >
                {config.subheading}
              </p>
            )}
          </div>
        )}
        <div className="cards-grid-grid gap-6">
          {cards.map((card, idx) => {
            const bgImage = card.background_image ? resolveImageUrl(card.background_image) : "";
            return (
              <div
                key={idx}
                className="relative flex flex-col"
                style={{
                  borderRadius: `${card.border_radius ?? 12}px`,
                  boxShadow: CARDS_GRID_SHADOW_PRESETS[card.card_shadow || "none"],
                }}
                data-testid={`cards-grid-public-card-${idx}`}
              >
                <div
                  className={`relative rounded-[inherit] flex flex-col flex-1 overflow-hidden ${bgImage ? "" : "bg-card"}`}
                  style={{
                    borderStyle: card.border_style || "solid",
                    borderWidth: `${card.border_width ?? 1}px`,
                    borderColor: card.border_color || "hsl(var(--border))",
                  }}
                >
                  {bgImage && (
                    <>
                      <div
                        className="absolute inset-0"
                        style={{
                          backgroundImage: `url(${bgImage})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          opacity: (card.background_image_opacity ?? 100) / 100,
                        }}
                      />
                      <div
                        className="absolute inset-0"
                        style={{ backgroundColor: `rgba(0,0,0,${(card.overlay_opacity ?? 45) / 100})` }}
                      />
                    </>
                  )}
                  <div className={`relative flex flex-col flex-1 p-6 gap-2 ${bgImage ? "text-white" : ""}`}>
                    {card.heading && (
                      <h3
                        className="font-bold text-lg"
                        style={{
                          fontFamily: "var(--font-heading, 'Playfair Display', serif)",
                          color: bgImage ? "#ffffff" : undefined,
                        }}
                        data-testid={`cards-grid-public-card-heading-${idx}`}
                      >
                        {card.heading}
                      </h3>
                    )}
                    {card.subheading && (
                      <p
                        className={`text-sm font-medium ${bgImage ? "text-white/90" : "text-muted-foreground"}`}
                        data-testid={`cards-grid-public-card-subheading-${idx}`}
                      >
                        {card.subheading}
                      </p>
                    )}
                    {card.rich_text && (
                      <RichHtml className="prose prose-sm max-w-none mt-1" html={card.rich_text} />
                    )}
                    {card.cta_text && <div className="mt-auto pt-4">{renderCta(card, idx)}</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
